// src/services/tokenMonitor.ts
import { authInterceptor } from "./authInterceptor";
import { logger } from "../../utils/logger";

class TokenMonitor {
  private refreshTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly REFRESH_THRESHOLD = 4 * 60 * 1000; // 4 minutes (refresh 1 minute before 15-minute expiry)

  startMonitoring() {
    this.scheduleRefresh();
  }

  stopMonitoring() {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
      logger.log("Token monitoring stopped");
    }
  }

  private scheduleRefresh() {
    const token = this.getAccessToken();
    if (!token) {
      // Debug logging removed for cleaner console
      return;
    }

    const payload = this.parseJWT(token);
    if (!payload || !payload.exp) {
      logger.log("Invalid token payload or missing expiration, skipping refresh scheduling");
      return;
    }

    const expirationTime = payload.exp * 1000; // Convert to milliseconds
    const currentTime = Date.now();
    const timeUntilExpiry = expirationTime - currentTime;
    const refreshTime = timeUntilExpiry - this.REFRESH_THRESHOLD;

    if (refreshTime > 0) {
      this.refreshTimer = setTimeout(() => {
        this.refreshTokens();
      }, refreshTime);
      
      // Debug logging removed for cleaner console
    } else {
      // Token expires soon, refresh immediately
      logger.log("Token expires soon, refreshing immediately");
      this.refreshTokens();
    }
  }

  private parseJWT(token: string) {
    try {
      // Validate token format
      if (!token || typeof token !== 'string') {
        console.warn("Invalid token: token is not a string or is empty");
        return null;
      }

      const parts = token.split(".");
      if (parts.length !== 3) {
        console.warn("Invalid JWT format: token should have 3 parts separated by dots");
        return null;
      }

      const base64Url = parts[1];
      if (!base64Url) {
        console.warn("Invalid JWT format: missing payload part");
        return null;
      }

      const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split("")
          .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
          .join("")
      );
      return JSON.parse(jsonPayload);
    } catch (error) {
      console.error("JWT parsing error:", error);
      return null;
    }
  }

  private async refreshTokens() {
    try {
      await authInterceptor.performRefresh();
      this.scheduleRefresh(); // Schedule next refresh
      // Debug logging removed for cleaner console
    } catch (error) {
      console.error("Token refresh failed:", error);
      authInterceptor.redirectToLogin();
    }
  }

  private getAccessToken(): string | null {
    return authInterceptor.getAccessToken();
  }
}

export const tokenMonitor = new TokenMonitor();
